import { combineReducers } from 'redux';
import { delay } from 'redux-saga';
import { takeEvery, call, select, race, take, put, takeLatest } from 'redux-saga/effects';
import { LOCATION_CHANGE } from 'react-router-redux';
import actionKey from '../utils/action-key';
import request from '../api-utils/request';
import SITE_MODES from '../../constants/site-modes';
import ASSESMENTS from '../../constants/assessments';
import { showSnackbarAction, dismissAction as dismissSnackbarAction } from './snackbar';
import { SET_ME } from './me';
import { APP_INITIALIZED } from './app';
import { LOGIN, SIGNUP, MERGE_ACCOUNT } from './auth';
import { trackPUAnalytics } from '../services/Mixpanel';

export const GET_QUESTIONS = 'assessments/GET_QUESTIONS';
export const ANSWER_QUESTION = 'assessments/ANSWER_QUESTION';
export const SAVE_RESPONSES = 'assessments/SAVE_RESPONSES';
export const HIGHLIGHT_QUESTIONS = 'assessments/HIGHLIGHT_QUESTIONS';
export const CLEAR_HIGHLIGHT_QUESTIONS = 'assessments/CLEAR_HIGHLIGHT_QUESTIONS';
export const CHANGE_FOCUS = 'assessments/CHANGE_FOCUS';
export const TOGGLE_COMPLETION_MODAL = 'assessments/TOGGLE_COMPLETION_MODAL';
export const SEND_COMPLETION_EMAIL = 'assessments/SEND_COMPLETION_EMAIL';
export const UPDATE_SCORE = 'assessments/UPDATE_SCORE';


export const questionsReducer = (state = null, action) => {
    switch (action.type) {
        case actionKey.init(GET_QUESTIONS):
        case actionKey.success(SAVE_RESPONSES):
            return null;
        case actionKey.success(GET_QUESTIONS): {
            if (action.assessment === 'values') {
                return action.payload.questions.filter(({ rankeditemid }) => (
                    // ignore question 17 (not sure why but it's like that on old platform)
                    rankeditemid !== 17
                )).map(({ rankeditemid, rankeditemtext }) => ({
                    rankeditemid,
                    rankeditemtext: rankeditemtext.replace(/^(\.)*/, ''),
                }));
            }
            return action.payload.questions;
        }
        default:
            return state;
    }
};


export const answersReducer = (state = {}, action) => {
    switch (action.type) {
        case actionKey.init(GET_QUESTIONS):
        case actionKey.success(SAVE_RESPONSES):
            return {};
        case ANSWER_QUESTION:
            return {
                ...state,
                [action.question]: action.answer,
            };
        default:
            return state;
    }
};

export const saveReducer = (state = {}, action) => {
    switch (action.type) {
        case SAVE_RESPONSES:
            return {
                loading: true,
            };
        case actionKey.init(GET_QUESTIONS):
        case actionKey.success(SAVE_RESPONSES):
            return {};
        case actionKey.failure(SAVE_RESPONSES):
            return {
                error: action.error,
            };
        default:
            return state;
    }
};

export const doneReducer = (state = false, action) => {
    switch (action.type) {
        case actionKey.init(GET_QUESTIONS):
        case LOCATION_CHANGE:
            return false;
        case actionKey.success(SAVE_RESPONSES):
            return true;
        default:
            return state;
    }
};

export const highlightReducer = (state = 0, action) => {
    switch (action.type) {
        case HIGHLIGHT_QUESTIONS:
            return state + 1;
        case CLEAR_HIGHLIGHT_QUESTIONS:
        case LOCATION_CHANGE:
            return 0;
        default:
            return state;
    }
};

export const questionsErrorReducer = (state = null, action) => {
    switch (action.type) {
        case actionKey.init(GET_QUESTIONS):
        case LOCATION_CHANGE:
            return null;
        case actionKey.failure(GET_QUESTIONS):
            return action.error;
        default:
            return state;
    }
};

export const currentAssessmentReducer = (state = null, action) => {
    switch (action.type) {
        case actionKey.init(GET_QUESTIONS):
            return action.assessment;
        case actionKey.failure(GET_QUESTIONS):
        case actionKey.success(SAVE_RESPONSES):
            return null;
        default:
            return state;
    }
};

export const completionModalReducer = (state = false, action) => {
    switch (action.type) {
        case TOGGLE_COMPLETION_MODAL:
            return !state;
        case actionKey.success(SEND_COMPLETION_EMAIL):
            return false;
        default:
            return state;
    }
};

export default combineReducers({
    questions: questionsReducer,
    answers: answersReducer,
    assessment: currentAssessmentReducer,
    save: saveReducer,
    done: doneReducer,
    highlight: highlightReducer,
    getQuestionsError: questionsErrorReducer,
    completionModal: completionModalReducer,
});

export const highlightQuestionsAction = () => ({
    type: HIGHLIGHT_QUESTIONS,
});
export const clearHighlightQuestionsAction = () => ({
    type: CLEAR_HIGHLIGHT_QUESTIONS,
});

export const answerQuestionAction = (question, answer) => ({
    type: ANSWER_QUESTION,
    question,
    answer,
});

export const saveResponsesAction = (assessment, answers) => ({
    type: SAVE_RESPONSES,
    assessment,
    answers,
});

export const getQuestionsAction = assessment => ({
    type: GET_QUESTIONS,
    assessment,
});

const updateUserScoreAction = ({ userId, assessment, scores }) => ({
    type: UPDATE_SCORE,
    payload: { userId, assessment, scores },
});

export const toggleCompletionModalAction = () => ({
    type: TOGGLE_COMPLETION_MODAL,
});

export const sendCompletionEmailAction = (payload, { resolve, reject }) => ({
    type: SEND_COMPLETION_EMAIL,
    payload,
    resolve,
    reject,
});

export function* rootSaga() {
    yield [
        takeEvery(GET_QUESTIONS, getQuestions),
        takeEvery(SAVE_RESPONSES, saveResponses),
        takeEvery(SAVE_RESPONSES, thisMayTakeAWhile),
        takeLatest([
            APP_INITIALIZED,
            actionKey.success(MERGE_ACCOUNT),
            actionKey.success(LOGIN),
            actionKey.success(SIGNUP),
        ], listenShowCompletionModal),
        takeEvery(SEND_COMPLETION_EMAIL, sendCompletionEmail),
        takeEvery(actionKey.success(SAVE_RESPONSES), updateScoresOnAssessmentDone),
    ];
}

const getAssessmentId = (assessment, siteMode) => {
    const assessmentIds = {
        personality: siteMode === SITE_MODES.EDUCATION ?
            ASSESMENTS.PERSONALITY : ASSESMENTS.PERSONALITY_HEXACO,
        interests: ASSESMENTS.INTERESTS,
        workplace: ASSESMENTS.WORKPLACE,
        purpose: ASSESMENTS.PURPOSE_SURVEY,
    };
    return assessmentIds[assessment] || '';
};


export function* updateScoresOnAssessmentDone({ payload, assessment }) {
    const { me } = yield select();
    const { scores } = payload;

    yield put(updateUserScoreAction({
        userId: me.userId,
        scores,
        assessment,
    }));
}

function* getQuestions({ type, assessment }) {
    let url;
    const { theme: { siteMode } } = yield select();
    if (assessment === 'values') {
        url = `/api/questions/ranked/${ASSESMENTS.VALUES}`;
    } else {
        url = `/api/questions/sections/${getAssessmentId(assessment, siteMode)}`;
    }
    // values
    yield call(request, {
        type,
        url,
        assessment,
    });
}


function* saveResponses({ type, assessment, answers }) {
    let body = answers;
    const { theme: { siteMode } } = yield select();
    if (assessment !== 'values') {
        body = Object.keys(answers).map(questionid => ({
            questionid,
            value: answers[questionid],
        }));
    }

    let url;

    if (assessment === 'personality' || assessment === 'workplace') {
        url = `/api/assessments/responses/avg/${getAssessmentId(assessment, siteMode)}`;
    } else if (assessment === 'interests' || assessment === 'purpose') {
        url = `/api/assessments/responses/sum/${getAssessmentId(assessment, siteMode)}`;
    } else if (assessment === 'values') {
        url = '/api/assessments/values';
    }

    yield call(request, {
        assessment,
        type,
        url,
        options: {
            method: 'POST',
            body,
        },
    });
    yield call(trackPUAnalytics, 'PathwayU:Finish Assessments', { type: assessment });
}

function* thisMayTakeAWhile() {
    const {
        showMessage,
    } = yield race({
        cancel: take([
            actionKey.success(SAVE_RESPONSES),
            actionKey.failure(SAVE_RESPONSES),
        ]),
        showMessage: delay(3000),
    });

    if (showMessage) {
        yield put(showSnackbarAction({
            message: 'Hold on, we are still processing your assesment',
            type: 'info',
            timeout: 99999,
        }));
    }
    yield take([actionKey.success(SAVE_RESPONSES), actionKey.failure(SAVE_RESPONSES)]);
    yield put(dismissSnackbarAction());
}


function* sendCompletionEmail({ type, payload, resolve, reject }) {
    const res = yield call(request, {
        url: '/api/assessments/completion-email',
        type,
        options: {
            body: payload,
            method: 'POST',
        },
    });

    if (res && res.error) {
        reject(res.error);
        return;
    }
    resolve(res.payload);
    yield put(showSnackbarAction({
        message: 'Email sent successfully',
        type: 'success',
        timeout: 2000,
    }));
}

export const shouldShowCompletionSend = ({ user, purposeSurvey, companyDomain }) => {
    let shouldBeDone = ['interests', 'values', 'personality', 'workplace'];
    if (companyDomain === 'reup') {
        shouldBeDone = ['interests', 'values'];
    }

    if (purposeSurvey) {
        shouldBeDone.push('purpose');
    }

    const allDone = shouldBeDone.every(assessment => (
        user && user[`${assessment}Completed`]
    ));

    return allDone;
};

// check if already completed all assessments
// if completed do not listen for modal show
// only on a first completion of all assessments
// we should show a modal
function* listenShowCompletionModal() {
    yield take(SET_ME);
    const { theme, users, me } = yield select();

    if (!theme.completionEmail) {
        return;
    }
    const isEmployer = theme.siteMode === SITE_MODES.EMPLOYER;

    if (isEmployer) {
        return;
    }
    const showCompletionSend = shouldShowCompletionSend({
        user: users.list[me.userId],
        purposeSurvey: theme.purposeSurvey,
    });

    if (showCompletionSend) {
        return;
    }
    // eslint-disable-next-line no-constant-condition
    while (true) {
        yield take(UPDATE_SCORE);
        const state = yield select();
        if (shouldShowCompletionSend({
            user: state.users.list[state.me.userId],
            purposeSurvey: theme.purposeSurvey,
            companyDomain: theme.companyDomain,
        })) {
            yield put(toggleCompletionModalAction());
            break;
        }
    }
}




// WEBPACK FOOTER //
// ./src/client/redux/assessments.js